import { stringToArray } from 'cron-converter';
import { DurationUnit } from '../interfaces/date';
import { convertMillisToNearestUnits } from './date';

export const TimeUnitIndexMap = { MINUTES: 0, HOURS: 1, DAY: 2, MONTH: 3, WEEKDAY: 4 };
const LevelMaxValue = {
  [TimeUnitIndexMap.MINUTES]: 60,
  [TimeUnitIndexMap.HOURS]: 24,
  [TimeUnitIndexMap.DAY]: 31,
  [TimeUnitIndexMap.MONTH]: 12,
  [TimeUnitIndexMap.WEEKDAY]: 7
};
const ALL_VALUES_INCLUDED = '*';

const validateSingleValueLevels = (levels: number[][], start: number, end: number) =>
  levels.slice(start, end).every(level => level.length === 1);

export function minIntervalValidation(cron: string, minInterval: number) {
  const cronArray = stringToArray(cron.trim());
  cron.split(' ').forEach((val, idx) => {
    if (val === ALL_VALUES_INCLUDED) cronArray[idx] = [];
  });

  const { value, unit } = convertMillisToNearestUnits(minInterval);
  const levelValidation = getLevelValidation(value, unit);
  return validateMinInterval(levelValidation.value, cronArray, levelValidation.level);
}

const getLevelValidation = (value: number, unit: DurationUnit) => {
  if (unit === DurationUnit.MINUTES && value < 60) {
    return { value, level: TimeUnitIndexMap.MINUTES };
  }

  if (unit === DurationUnit.MINUTES || (unit === DurationUnit.HOURS && value < 24)) {
    return {
      value: unit === DurationUnit.MINUTES ? Math.ceil(value / 60) : value,
      level: TimeUnitIndexMap.HOURS
    };
  }

  if (unit === DurationUnit.HOURS || (unit === DurationUnit.DAYS && value < 31)) {
    return {
      value: unit === DurationUnit.HOURS ? Math.ceil(value / 24) : value,
      level: TimeUnitIndexMap.DAY
    };
  }

  return { value: Math.ceil(value / 31), level: TimeUnitIndexMap.MONTH };
};

const validateMinInterval = (minInterval: number, cronArray: number[][], level: number) => {
  if (!validateSingleValueLevels(cronArray, 0, level)) {
    return false;
  }

  if (level === TimeUnitIndexMap.DAY) {
    return validateDays(minInterval, cronArray);
  }

  if (level === TimeUnitIndexMap.MONTH && cronArray[TimeUnitIndexMap.WEEKDAY].length) {
    return false;
  }

  return validateLevel(minInterval, cronArray, level);
};

const minDifferenceInNextLevel = (level: number, cronArray: number[][]) => {
  if (level === cronArray.length || level === cronArray.length - 1) {
    return 0;
  }

  const levelArray = cronArray[level];

  if (!levelArray.length) {
    return 0;
  }

  if (levelArray.length === 1) {
    return LevelMaxValue[level];
  }

  const minInterval = minDifferenceAtLevel(levelArray);
  return minInterval - 1;
};

const minDifferenceAtLevel = (levelArray: number[]) => {
  let minInterval = Infinity;
  for (let i = 1; i < levelArray.length; i += 1) {
    const diff = levelArray[i] - levelArray[i - 1];
    if (minInterval > diff) {
      minInterval = diff;
    }
  }
  return minInterval;
};

const validateCyclicInterval = (cronArray: number[][], minInterval: number, level: number) => {
  const diffInNextLevel = minDifferenceInNextLevel(level + 1, cronArray);

  const levelArray = cronArray[level];

  return (
    (diffInNextLevel + 1) * LevelMaxValue[level] +
      levelArray[0] -
      levelArray[levelArray.length - 1] >=
    minInterval
  );
};

const validateLevel = (minInterval: number, cronArray: number[][], level: number) => {
  const levelArray = cronArray[level];

  if (!levelArray.length) {
    return minInterval === 1;
  }

  const minDifference = minDifferenceAtLevel(levelArray);
  if (minDifference < minInterval) {
    return false;
  }

  return validateCyclicInterval(cronArray, minInterval, level);
};

const validateDays = (minInterval: number, cronArray: number[][]) => {
  const days = cronArray[TimeUnitIndexMap.DAY];
  const weekdays = cronArray[TimeUnitIndexMap.WEEKDAY];

  if (!days.length && !weekdays.length) {
    return minInterval === 1;
  }

  return validateLevel(
    minInterval,
    cronArray,
    weekdays.length ? TimeUnitIndexMap.WEEKDAY : TimeUnitIndexMap.DAY
  );
};

// max interval for cron validation

export function maxIntervalValidation(cron: string, maxInterval: number) {
  const cronArray = stringToArray(cron.trim());
  cron.split(' ').forEach((val, idx) => {
    if (val === ALL_VALUES_INCLUDED) cronArray[idx] = [];
  });

  const { value, unit } = convertMillisToNearestUnits(maxInterval);
  const levelValidation = getLevelValidation(value, unit);
  return validateMaxInterval(levelValidation.value, cronArray, levelValidation.level);
}

const validateMaxInterval = (maxInterval: number, cronArray: number[][], level: number) => {
  if (!validateSingleValueLevelsForMax(cronArray, level + 1, cronArray.length - 1)) {
    return false;
  }

  if (level === TimeUnitIndexMap.DAY) {
    return validateDaysForMax(maxInterval, cronArray);
  }

  return validateLevelForMax(maxInterval, cronArray, level);
};

export const validateSingleValueLevelsForMax = (levels: number[][], start: number, end: number) =>
  levels
    .slice(start, end)
    .every((level, index) => level.length === 0 || level.length === LevelMaxValue[index]);

export const validateLevelForMax = (maxInterval: number, cronArray: number[][], level: number) => {
  const levelArray = cronArray[level];

  if (!levelArray.length) {
    return maxInterval >= 1;
  }

  if (levelArray.length === 1) {
    return maxInterval > LevelMaxValue[level] - 1;
  }

  const maxDifference = maxDifferenceAtLevel(levelArray);
  if (maxDifference > maxInterval) {
    return false;
  }

  return validateCyclicIntervalForMax(cronArray, maxInterval, level);
};

export const maxDifferenceAtLevel = (levelArray: number[]) => {
  let max = 0;
  for (let i = 1; i < levelArray.length; i += 1) {
    const diff = levelArray[i] - levelArray[i - 1];
    max = Math.max(diff, max);
  }
  return max;
};

export const validateDaysForMax = (maxInterval: number, cronArray: number[][]) => {
  const days = cronArray[TimeUnitIndexMap.DAY];
  const weekdays = cronArray[TimeUnitIndexMap.WEEKDAY];

  if (!days.length && !weekdays.length) {
    return maxInterval >= 1;
  }

  return validateLevelForMax(
    maxInterval,
    cronArray,
    weekdays.length ? TimeUnitIndexMap.WEEKDAY : TimeUnitIndexMap.DAY
  );
};

export const validateCyclicIntervalForMax = (
  cronArray: number[][],
  maxInterval: number,
  level: number
) => {
  const levelArray = cronArray[level];

  return LevelMaxValue[level] + levelArray[0] - levelArray[levelArray.length - 1] <= maxInterval;
};
